import { HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  Injectable,
  Provider,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  Translation,
  TranslocoLoader,
  TranslocoService,
  provideTransloco,
} from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { Observable, firstValueFrom } from 'rxjs';
import { LocaleService } from './locale.service';

/**
 * TranslocoHttpLoader — fetches `public/i18n/<lang>.json` at runtime.
 *
 * Runtime JSON catalogues (not compile-time `$localize`) are the deliberate
 * choice here: the platform targets ~22 languages, so we want ONE build with
 * the active language loaded at runtime — adding a language is dropping in a
 * new `<lang>.json`, never a rebuild — and an in-app language switch. This
 * mirrors the sibling `wip-map` app's Transloco stack so the two employee
 * surfaces share key conventions and translation tooling.
 */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<Translation> {
    return this.http.get<Translation>(`/i18n/${lang}.json`);
  }
}

/**
 * AVAILABLE_LANGS grows one entry per shipped `public/i18n/<id>.json`. English
 * is the source-of-truth catalogue and the fallback; every other locale file
 * is written by the translation portal's pull job (translation-sync.yml,
 * rdx-translate.config.json), never by hand. Adding a locale: add it to the
 * portal project, to `locales` in rdx-translate.config.json, here, to the
 * messageformat list below and to SUPPORTED_LOCALES in locale.service.ts.
 */
const AVAILABLE_LANGS = [
  { id: 'en', label: 'English' },
  { id: 'ja', label: '日本語' },
];
const DEFAULT_LANG = 'en';
const FALLBACK_LANG = 'en';

/**
 * App-level Transloco providers. Spread into ApplicationConfig.providers.
 *
 * NOTE: HttpClient is intentionally NOT provided here — `provideGraphQL()`
 * already calls `provideHttpClient(withInterceptorsFromDi())`, and the loader
 * injects that same client. Calling `provideHttpClient()` twice would
 * re-register the interceptor chain.
 */
export function provideAppTransloco(): (Provider | EnvironmentProviders)[] {
  return [
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGS,
        defaultLang: DEFAULT_LANG,
        fallbackLang: FALLBACK_LANG,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: isDevMode(),
          useFallbackTranslation: true,
          allowEmpty: false,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    // ICU messageformat — plurals ({count, plural, …}) and number formatting.
    // Add each shipped locale's tag here alongside AVAILABLE_LANGS.
    provideTranslocoMessageformat({
      locales: ['en-GB', 'ja'],
    }),
    // Paint in the language the user's profile gave last time on this device
    // (English on a first visit) and load its catalogue before first paint.
    // LocaleService then applies the signed-in viewer's profile language —
    // the only source; see locale.service.ts.
    // The `| transloco` pipe tolerates async loading, but components that call
    // `TranslocoService.translate()` imperatively (e.g. table columns / select
    // options built in a computed) run at construction — without this they
    // race the HTTP load and emit "missing translation". Blocking bootstrap on
    // the catalogue also avoids a flash of translation keys on the first
    // render. If that fails, load the default language so the app still boots.
    provideAppInitializer(async () => {
      const transloco = inject(TranslocoService);
      const locale = inject(LocaleService);
      try {
        await locale.initialise();
      } catch (err) {
        console.warn('[transloco] locale detection failed, using the default language:', err);
        await firstValueFrom(transloco.load(transloco.getActiveLang()));
      }
    }),
  ];
}
